import React, { useEffect, useState } from 'react';
import {
  DataGrid,
  GridColDef,
} from '@mui/x-data-grid';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Grid,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import LockResetIcon from '@mui/icons-material/LockReset';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';

interface Tenant {
  id: number;
  company_name: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  is_active: boolean;
}

interface UserCredentials {
  username: string;
  password: string;
  message: string;
}

export const TenantManagement: React.FC<{ token: string }> = ({ token }) => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openForm, setOpenForm] = useState(false);
  const [editTenant, setEditTenant] = useState<Tenant | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [userCredentials, setUserCredentials] = useState<UserCredentials | null>(null);

  // Form fields
  const [companyName, setCompanyName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('');
  const [postalCode, setPostalCode] = useState('');

  const fetchTenants = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/tenants`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch companies');
      setTenants(await res.json());
    } catch (err: any) {
      setError(err.message || 'Error loading companies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, [token]);

  const handleOpenCreate = () => {
    setEditTenant(null);
    setCompanyName('');
    setContactPerson('');
    setEmail('');
    setPhone('');
    setAddress('');
    setCity('');
    setState('');
    setCountry('');
    setPostalCode('');
    setUserCredentials(null);
    setOpenForm(true);
  };

  const handleOpenEdit = (tenant: Tenant) => {
    setEditTenant(tenant);
    setCompanyName(tenant.company_name);
    setContactPerson(tenant.contact_person || '');
    setEmail(tenant.email || '');
    setPhone(tenant.phone || '');
    setAddress(tenant.address || '');
    setCity(tenant.city || '');
    setState(tenant.state || '');
    setCountry(tenant.country || '');
    setPostalCode(tenant.postal_code || '');
    setUserCredentials(null);
    setOpenForm(true);
  };

  const handleSubmit = async () => {
    if (formLoading) return; // Prevent multiple clicks
    
    setFormLoading(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const data = {
        company_name: companyName,
        contact_person: contactPerson,
        email: email,
        phone: phone,
        address: address,
        city: city,
        state: state,
        country: country,
        postal_code: postalCode,
      };

      const url = editTenant
        ? `${process.env.REACT_APP_API_URL}/auth/tenants/${editTenant.id}`
        : `${process.env.REACT_APP_API_URL}/auth/tenants`;

      const res = await fetch(url, {
        method: editTenant ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || 'Failed to save company');
      }

      const result = await res.json();
      
      // Check if user credentials were created
      if (result._user_created) {
        setUserCredentials(result._user_created);
        setFormLoading(false); // Re-enable for credential confirmation
      } else {
        setSuccessMsg(editTenant ? 'Company updated successfully' : 'Company created successfully');
        setOpenForm(false);
        await fetchTenants();
        setFormLoading(false);
      }
    } catch (err: any) {
      setError(err.message || 'Error saving company');
      setFormLoading(false); // Re-enable only on error
    }
  };

  const handleResetPassword = async (tenantId: number) => {
    if (!window.confirm('Reset password for this company user?')) return;

    if (formLoading) return; // Prevent multiple clicks
    
    setFormLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/tenants/${tenantId}/reset-password`,
        {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to reset password');
      }

      const result = await res.json();
      setUserCredentials({
        username: result.username,
        password: result.password,
        message: result.message,
      });
      setOpenForm(true); // Open dialog to show credentials
      setFormLoading(false); // Re-enable after success
    } catch (err: any) {
      setError(err.message || 'Error resetting password');
      setFormLoading(false); // Re-enable only on error
    }
  };

  const handleDeleteTenant = async (tenantId: number, companyName: string) => {
    if (!window.confirm(`Are you sure you want to delete company "${companyName}"? This action cannot be undone.`)) return;

    setError(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/tenants/${tenantId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to delete company');
      }

      setSuccessMsg('Company deleted successfully');
      await fetchTenants();
    } catch (err: any) {
      setError(err.message || 'Error deleting company');
    }
  };

  const columns: GridColDef[] = [
    { field: 'company_name', headerName: 'Company Name', width: 200 },
    { field: 'contact_person', headerName: 'Contact Person', width: 150 },
    { field: 'email', headerName: 'Email', width: 200 },
    { field: 'phone', headerName: 'Phone', width: 130 },
    { field: 'city', headerName: 'City', width: 120 },
    { field: 'country', headerName: 'Country', width: 120 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <>
          <IconButton size="small" onClick={() => handleOpenEdit(params.row)} color="primary">
            <EditIcon />
          </IconButton>
          <IconButton size="small" onClick={() => handleResetPassword(params.row.id)} color="secondary">
            <LockResetIcon />
          </IconButton>
          <IconButton size="small" onClick={() => handleDeleteTenant(params.row.id, params.row.company_name)} color="error">
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Company Management</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <IconButton onClick={fetchTenants} disabled={loading}>
            <RefreshIcon />
          </IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
            Add Company
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}

      {loading ? (
        <CircularProgress />
      ) : (
        <div style={{ height: 500, width: '100%' }}>
          <DataGrid
            rows={tenants}
            columns={columns}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
          />
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openForm} onClose={() => !userCredentials && setOpenForm(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {userCredentials ? 'Password Reset Successful' : editTenant ? 'Edit Company' : 'Add New Company'}
        </DialogTitle>
        <DialogContent>
          {/* User Credentials Display */}
          {userCredentials ? (
            <Paper sx={{ p: 3, mb: 2, bgcolor: 'warning.light', border: 2, borderColor: 'warning.main' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                ⚠️ New Login Credentials
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {userCredentials.message || 'Please save these credentials. They will not be shown again!'}
              </Alert>
              <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="body1" sx={{ mb: 1, fontFamily: 'monospace', fontSize: '1.1rem' }}>
                  <strong>Username:</strong> {userCredentials.username}
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '1.1rem' }}>
                  <strong>Password:</strong> {userCredentials.password}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    navigator.clipboard.writeText(`Username: ${userCredentials.username}\nPassword: ${userCredentials.password}`);
                  }}
                  fullWidth
                >
                  Copy Credentials
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    setUserCredentials(null);
                    setOpenForm(false);
                    fetchTenants();
                  }}
                  fullWidth
                >
                  OK, I Saved It
                </Button>
              </Box>
            </Paper>
          ) : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Company Name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                fullWidth
                required
                disabled={!!editTenant}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Contact Person"
                value={contactPerson}
                onChange={(e) => setContactPerson(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="City"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="State"
                value={state}
                onChange={(e) => setState(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Postal Code"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                fullWidth
              />
            </Grid>
          </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)} disabled={formLoading || !!userCredentials}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={formLoading || !companyName || !!userCredentials}>
            {formLoading ? <CircularProgress size={20} /> : editTenant ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
